import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import StepQualification from "./StepQualification";
import { Container } from "reactstrap";
import { useNotaire } from "../context/NotaireContext";
import { Heading } from "../components/catalyst/heading";
import { API_BASE } from "../config";

import {
  HomeIcon,
  PhoneIcon,
  HeartIcon,
  UsersIcon,
  TagIcon,
  BuildingOfficeIcon,
  DocumentCheckIcon,
} from "@heroicons/react/20/solid";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { CheckIcon } from "@heroicons/react/24/solid";

// Mapping clé string (API) → composant icône local
const ICON_MAP = {
  home:     HomeIcon,
  heart:    HeartIcon,
  users:    UsersIcon,
  tag:      TagIcon,
  building: BuildingOfficeIcon,
  document: DocumentCheckIcon,
};

const CATEGORY_ORDER = [
  "Comptabilité",
  "Fiscalité",
  "Social & RH",
  "Juridique & Création",
  "Conseil & Financement",
];

const MOTIFS_COMPTABLE = [
  { value: "bilan_annuel",            label: "Bilan annuel",               category: "Comptabilité",         icon: "document" },
  { value: "tenue_comptable",         label: "Tenue comptable",            category: "Comptabilité",         icon: "document" },
  { value: "declaration_tva",         label: "Déclaration TVA",            category: "Comptabilité",         icon: "document" },
  { value: "revision_comptes",        label: "Révision des comptes",       category: "Comptabilité",         icon: "document" },
  { value: "liasse_fiscale",          label: "Liasse fiscale",             category: "Fiscalité",            icon: "tag" },
  { value: "declaration_is_ir",       label: "Déclaration IS / IR",        category: "Fiscalité",            icon: "tag" },
  { value: "optimisation_fiscale",    label: "Optimisation fiscale",       category: "Fiscalité",            icon: "tag" },
  { value: "controle_fiscal",         label: "Assistance contrôle fiscal", category: "Fiscalité",            icon: "tag" },
  { value: "embauche",                label: "Embauche (DPAE)",            category: "Social & RH",          icon: "users" },
  { value: "paie",                    label: "Paie & charges sociales",    category: "Social & RH",          icon: "users" },
  { value: "rupture_conventionnelle", label: "Rupture conventionnelle",    category: "Social & RH",          icon: "users" },
  { value: "arret_travail",           label: "Arrêt de travail / AT",      category: "Social & RH",          icon: "users" },
  { value: "creation_entreprise",     label: "Création d'entreprise",      category: "Juridique & Création", icon: "building" },
  { value: "changement_statut",       label: "Changement de statut",       category: "Juridique & Création", icon: "building" },
  { value: "cession_fonds",           label: "Cession de fonds / parts",   category: "Juridique & Création", icon: "building" },
  { value: "dissolution",             label: "Dissolution / liquidation",   category: "Juridique & Création", icon: "building" },
  { value: "previsionnel",            label: "Prévisionnel / budget",      category: "Conseil & Financement", icon: "home" },
  { value: "financement",             label: "Financement (BPI / banque)", category: "Conseil & Financement", icon: "home" },
  { value: "rachat_entreprise",       label: "Rachat d'entreprise",        category: "Conseil & Financement", icon: "home" },
  { value: "premier_rdv",             label: "Premier rendez-vous",        category: "Conseil & Financement", icon: "heart" },
];

// ─── Statuts sidebar ─────────────────────────────────────────────────────────
function StatusBadge({ ok, loading }) {
  if (loading) return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-500">
      <span className="h-2 w-2 rounded-full bg-gray-400 animate-pulse" />
      Vérification…
    </span>
  );
  if (ok) return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
      <span className="h-2 w-2 rounded-full bg-green-500" />
      API opérationnelle
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-medium text-red-700">
      <span className="h-2 w-2 rounded-full bg-red-500" />
      API non disponible
    </span>
  );
}

function WidgetStatusBadge({ count }) {
  if (count === null) return null;
  if (count > 0) return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700">
      <span className="h-2 w-2 rounded-full bg-green-500" />
      Widget actif — {count} RDV pris
    </span>
  );
}

// ─── Modal prévisualisation ───────────────────────────────────────────────────
function PreviewModal({ notaireId, color = '#4f46e5', onClose }) {
  const [step, setStep]                   = useState(1);
  const [rendezvousType, setRdvType]      = useState("telephone");
  const [selectedMotif, setMotif]         = useState(null);
  const [qualificationData, setQualData]  = useState({});

  // créneaux
  const [days, setDays]                   = useState([]);
  const [slotsLoading, setSlotsLoading]   = useState(false);
  const [startOffset, setStartOffset]     = useState(0);
  const [availableDayCount, setAvailableCount] = useState(0);
  const [showAll, setShowAll]             = useState(false);

  const motifs = MOTIFS_COMPTABLE;
  const motifsLoading = false;

  // fermer avec Échap
  useEffect(() => {
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [onClose]);

  // charger les créneaux (step 1 uniquement)
  useEffect(() => {
    if (!notaireId) return;
    let cancelled = false;
    setSlotsLoading(true);
    fetch(`${API_BASE}/api/slots/${notaireId}?startOffset=${startOffset}&pageSize=3`)
      .then((r) => r.json())
      .then((data) => {
        if (cancelled) return;
        setDays(Array.isArray(data?.days) ? data.days : []);
        setAvailableCount(Number(data?.availableDayCount ?? 0));
      })
      .catch(() => { if (!cancelled) setDays([]); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
  }, [notaireId, startOffset]);

  const canGoPrev = startOffset > 0;
  const canGoNext = startOffset + 3 < availableDayCount;
  const hasMore   = days.some((d) => d.slots.length > 3);

  const grouped = useMemo(() => {
    const map = motifs.reduce((acc, m) => {
      if (!acc[m.category]) acc[m.category] = [];
      acc[m.category].push(m);
      return acc;
    }, {});
    return CATEGORY_ORDER.filter((c) => map[c]).map((c) => ({ category: c, items: map[c] }));
  }, [motifs]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.55)" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative flex flex-col w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[92vh]">

        {/* ── En-tête fixe ── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2">
            {/* icône calendrier */}
            <svg className="h-5 w-5 text-indigo-700" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
            </svg>
            <span className="text-base font-semibold text-gray-900">Prenez rendez-vous</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-semibold text-indigo-700">
              Aperçu
            </span>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Contenu scrollable ── */}
        <div className="overflow-y-auto px-6 py-6 flex-1">

          {/* ════════════ STEP 1 — Créneaux ════════════ */}
          {step === 1 && (
            <div className="flex flex-col gap-6">
              {/* Type de RDV */}
              <div>
                <p className="text-sm font-bold mb-2">Type de rendez-vous souhaité</p>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { value: "presentiel", label: "Cabinet",  Icon: HomeIcon },
                    { value: "telephone",  label: "Téléphone",   Icon: PhoneIcon },
                  ].map(({ value, label, Icon }) => (
                    <button
                      key={value}
                      onClick={() => setRdvType(value)}
                      className={`flex items-center justify-center gap-2 rounded-md border px-4 py-3 text-sm font-medium transition-colors
                        ${rendezvousType === value
                          ? "bg-black text-white border-black"
                          : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-black hover:text-white"}`}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Grille de créneaux */}
              <div className="rounded-2xl border border-sky-100 bg-white px-4 py-5 shadow-sm">
                {/* Navigation */}
                <div className="flex items-center justify-between gap-2 mb-4">
                  <button
                    disabled={!canGoPrev}
                    onClick={() => { if (canGoPrev) setStartOffset((o) => Math.max(0, o - 3)); }}
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-sky-600 hover:bg-sky-50 transition-colors ${!canGoPrev ? "opacity-30 cursor-default" : ""}`}
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <p className="flex-1 text-center text-sm font-semibold text-slate-700">
                    Prochaines disponibilités
                  </p>
                  <button
                    disabled={!canGoNext}
                    onClick={() => { if (canGoNext) setStartOffset((o) => o + 3); }}
                    className={`flex h-8 w-8 items-center justify-center rounded-full text-sky-600 hover:bg-sky-50 transition-colors ${!canGoNext ? "opacity-30 cursor-default" : ""}`}
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>

                {slotsLoading ? (
                  <div className="grid grid-cols-3 gap-4">
                    {[0, 1, 2].map((i) => (
                      <div key={i} className="space-y-2">
                        <div className="h-5 w-16 rounded bg-gray-100 animate-pulse mx-auto" />
                        <div className="h-4 w-12 rounded bg-gray-100 animate-pulse mx-auto" />
                        {[0, 1, 2].map((j) => (
                          <div key={j} className="h-9 rounded-3xl bg-sky-50 animate-pulse" />
                        ))}
                      </div>
                    ))}
                  </div>
                ) : days.length === 0 ? (
                  <p className="text-center text-sm text-slate-400 py-6">
                    Aucun créneau disponible. Assurez-vous que votre calendrier est synchronisé.
                  </p>
                ) : (
                  <>
                    <div className="grid grid-cols-3 gap-4 text-center text-sm">
                      {days.map((day) => (
                        <div key={day.date} className="space-y-2">
                          <div>
                            <p className="text-sm font-semibold text-slate-800 mb-0 capitalize">{day.label}</p>
                            <p className="text-xs text-slate-500">{day.dateLabel}</p>
                          </div>
                          {day.slots.length === 0 ? (
                            <p className="mt-6 text-base text-slate-400">—</p>
                          ) : (
                            <div className="space-y-2">
                              {(showAll ? day.slots : day.slots.slice(0, 3)).map(({ label, slot }) => (
                                <button
                                  key={`${day.date}-${slot.start}`}
                                  type="button"
                                  onClick={() => setStep(2)}
                                  className="flex w-full items-center justify-center rounded-3xl px-3 py-2 text-sm font-semibold transition-colors bg-sky-100 text-sky-700 hover:bg-sky-200"
                                >
                                  {label}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                    {hasMore && (
                      <div className="mt-4 text-center">
                        <button
                          onClick={() => setShowAll((v) => !v)}
                          className="text-sm font-semibold text-sky-600 hover:underline"
                        >
                          {showAll ? "Voir moins de créneaux" : "Voir plus de créneaux"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* ════════════ STEP 2 — Motifs ════════════ */}
          {step === 2 && (
            <div>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                ← Retour aux créneaux
              </button>

              <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">
                Pourquoi souhaitez-vous prendre rendez-vous ?
              </h2>

              {motifsLoading ? (
                <div className="space-y-4 mt-6">
                  {[1, 2, 3].map((i) => (
                    <div key={i}>
                      <div className="h-5 w-28 rounded-full bg-indigo-100 animate-pulse mb-3" />
                      <div className="grid grid-cols-2 gap-2">
                        {[1, 2, 3, 4].map((j) => <div key={j} className="h-16 rounded-2xl bg-gray-100 animate-pulse" />)}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
              <div className="space-y-6 mt-6">
                {grouped.map(({ category, items }) => (
                  <div key={category}>
                    <div className="mb-3">
                      <span className="inline-flex rounded-full bg-indigo-100 px-2.5 py-1 text-xs font-semibold text-indigo-700">
                        {category}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                      {items.map((motif) => {
                        const Icon = ICON_MAP[motif.icon] ?? HomeIcon;
                        const isSelected = selectedMotif === motif.value;
                        return (
                          <button
                            key={motif.value}
                            type="button"
                            onClick={() => { setMotif(motif.value); setQualData({}); setStep(3); }}
                            className={`group w-full rounded-2xl border text-left p-3 transition-all focus:outline-none
                              ${isSelected
                                ? "border-indigo-600 bg-indigo-600 text-white shadow-md"
                                : "border-gray-200 bg-white text-gray-900 shadow-sm hover:border-indigo-300 hover:bg-indigo-50"}`}
                          >
                            <div className="flex items-center gap-3">
                              <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl p-2 transition-colors
                                ${isSelected ? "bg-white/15" : "bg-indigo-100 group-hover:bg-indigo-200"}`}>
                                <Icon className={`h-5 w-5 ${isSelected ? "text-white" : "text-indigo-700"}`} />
                              </div>
                              <span className="text-sm font-semibold leading-5">{motif.label}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          )}

          {/* ════════════ STEP 3 — Qualification ════════════ */}
          {step === 3 && (
            <StepQualification
              qualificationData={qualificationData}
              onNext={(data) => { setQualData(data); setStep(4); }}
              onBack={() => setStep(2)}
            />
          )}

          {/* ════════════ STEP 4 — Formulaire ════════════ */}
          {step === 4 && (
            <div>
              <button
                type="button"
                onClick={() => setStep(3)}
                className="mb-4 flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
              >
                ← Retour aux motifs
              </button>

              <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
                Vos coordonnées
              </h2>

              <div className="flex flex-col gap-3">
                <div className="flex gap-2">
                  <input disabled placeholder="Nom"    className="flex-1 rounded-md border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-400 placeholder-gray-300" />
                  <input disabled placeholder="Prénom" className="flex-1 rounded-md border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-400 placeholder-gray-300" />
                </div>
                <input disabled placeholder="Email"     className="w-full rounded-md border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-400 placeholder-gray-300" />
                <input disabled placeholder="Téléphone" className="w-full rounded-md border border-gray-200 bg-white px-3 py-2.5 text-sm text-gray-400 placeholder-gray-300" />

                <button
                  disabled
                  className="mx-auto mt-2 flex items-center justify-center gap-2 rounded-md px-6 py-2.5 text-sm font-medium text-white opacity-50 cursor-not-allowed"
                  style={{ backgroundColor: color }}
                >
                  <CheckIcon className="h-4 w-4" />
                  Valider
                </button>

                <p className="mt-1 text-center text-xs text-gray-400">
                  Mode aperçu — aucune confirmation ne sera envoyée
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const PRESET_COLORS = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#f43f5e', '#1e293b'];

const PROD_BASE = 'https://cloterenotaire.onrender.com';

// ─── Page principale ──────────────────────────────────────────────────────────
function WidgetRdv() {
  const context = useNotaire();

  const notaireId = context?.member?.metaData?.airtable_id ?? "";
  const userEmail = context?.user?.email ?? "";

  // URLs toujours en prod (destinées à être partagées)
  const bookingUrl = `${PROD_BASE}/booking/${notaireId}`;
  const snippet    = `<script src="${PROD_BASE}/widget/clotere-widget.js"\n        data-notaire-id="${notaireId}"></script>`;

  const [btnMode, setBtnMode]           = useState('floating');
  const [btnColor, setBtnColor]         = useState('#4f46e5');
  const [btnLabel, setBtnLabel]         = useState('Prendre RDV');
  const [configLoaded, setConfigLoaded] = useState(false);
  const [saveStatus, setSaveStatus]     = useState('idle');
  const configJustLoaded                = useRef(false);
  const saveTimer                       = useRef(null);
  const [installTab, setInstallTab]     = useState('web');

  const [copied, setCopied]       = useState(false);
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [copiedMsg, setCopiedMsg] = useState(false);
  const [apiOk, setApiOk]                   = useState(null);
  const [apiLoading, setApiLoading]         = useState(true);
  const [widgetRdvCount, setWidgetRdvCount] = useState(null);
  const [emailContact, setEmailContact]     = useState("");
  const [sendingEmail, setSendingEmail]     = useState(false);
  const [emailSent, setEmailSent]           = useState(false);
  const [emailError, setEmailError]         = useState("");
  const [showPreview, setShowPreview]       = useState(false);

  // Charger la config widget depuis le serveur
  useEffect(() => {
    if (!notaireId) return;
    fetch(`${API_BASE}/api/widget-config/${notaireId}`)
      .then((r) => r.ok ? r.json() : {})
      .then((cfg) => {
        configJustLoaded.current = true;
        if (cfg.mode)  setBtnMode(cfg.mode);
        if (cfg.color) setBtnColor(cfg.color);
        if (cfg.label) setBtnLabel(cfg.label);
      })
      .catch(() => {})
      .finally(() => setConfigLoaded(true));
  }, [notaireId]);

  // Auto-save avec debounce 800 ms (ignoré lors du chargement initial)
  useEffect(() => {
    if (!configLoaded || !notaireId) return;
    if (configJustLoaded.current) { configJustLoaded.current = false; return; }
    clearTimeout(saveTimer.current);
    setSaveStatus('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_BASE}/api/widget-config/${notaireId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ mode: btnMode, color: btnColor, label: btnLabel }),
        });
        if (!res.ok) throw new Error();
        setSaveStatus('saved');
        setTimeout(() => setSaveStatus('idle'), 2000);
      } catch {
        setSaveStatus('error');
      }
    }, 800);
    return () => clearTimeout(saveTimer.current);
  }, [btnMode, btnColor, btnLabel, configLoaded, notaireId]);

  useEffect(() => {
    if (!notaireId) return;
    setApiLoading(true);
    fetch(`${API_BASE}/api/slots/${notaireId}?startOffset=0&pageSize=1`)
      .then((r) => { setApiOk(r.ok); })
      .catch(() => setApiOk(false))
      .finally(() => setApiLoading(false));
  }, [notaireId]);

  useEffect(() => {
    if (!Array.isArray(context?.demandeRdv)) return;
    setWidgetRdvCount(
      context.demandeRdv.filter((r) => r?.fields?.provenance === "widget").length
    );
  }, [context?.demandeRdv]);

  const handleCopy = () => {
    navigator.clipboard.writeText(snippet).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  };

  const handleSendEmail = async (e) => {
    e.preventDefault();
    if (!emailContact) return;
    setSendingEmail(true);
    setEmailError("");
    try {
      const res = await fetch(`${API_BASE}/api/widget/send-install-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ to: emailContact, from: userEmail, notaireId, snippet }),
      });
      if (!res.ok) throw new Error();
      setEmailSent(true);
      setEmailContact("");
      setTimeout(() => setEmailSent(false), 4000);
    } catch {
      setEmailError("L'envoi a échoué. Vérifiez l'adresse ou réessayez.");
    } finally {
      setSendingEmail(false);
    }
  };

  const handleClosePreview = useCallback(() => setShowPreview(false), []);

  if (!context?.user) return null;

  return (
    <Container>
      {showPreview && (
        <PreviewModal notaireId={notaireId} color={btnColor} onClose={handleClosePreview} />
      )}

      {/* En-tête */}
      <div className="flex w-full flex-wrap items-end justify-between gap-4 border-b border-zinc-950/10 pb-6 dark:border-white/10">
        <div>
          <Heading>Widget Rendez-vous</Heading>
          <p className="mt-1 text-sm text-gray-500">
            Intégrez la prise de rendez-vous Clotere directement sur votre site web.
          </p>
        </div>
        <WidgetStatusBadge count={widgetRdvCount} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-3">
        {/* Colonne principale */}
        <div className="lg:col-span-2 space-y-6">

          {/* Configurateur — commun à tous les canaux */}
          <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between mb-5">
              <h2 className="text-base font-semibold text-gray-900">Personnaliser votre bouton</h2>
              {saveStatus === 'saving' && (
                <span className="text-xs text-gray-400 flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-gray-300 animate-pulse" />
                  Sauvegarde…
                </span>
              )}
              {saveStatus === 'saved' && (
                <span className="text-xs text-green-600 flex items-center gap-1.5">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                  Sauvegardé
                </span>
              )}
              {saveStatus === 'error' && (
                <span className="text-xs text-red-500">Erreur — réessayez</span>
              )}
            </div>

            {/* Type de bouton */}
            <p className="text-sm font-medium text-gray-700 mb-3">Type de bouton</p>
            <div className="grid grid-cols-2 gap-3 mb-6">
              {[
                {
                  value: 'floating',
                  label: 'Flottant',
                  desc: 'Bouton fixe en bas à droite de l\'écran',
                  icon: (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25" />
                    </svg>
                  ),
                },
                {
                  value: 'inline',
                  label: 'Intégré',
                  desc: 'Bouton statique dans le contenu de votre page',
                  icon: (
                    <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 6.75h16.5M3.75 12h16.5M12 17.25h8.25" />
                    </svg>
                  ),
                },
              ].map(({ value, label, desc, icon }) => {
                const active = btnMode === value;
                return (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setBtnMode(value)}
                    className={`flex flex-col items-start gap-2 rounded-xl border-2 p-4 text-left transition-all
                      ${active
                        ? 'border-indigo-600 bg-indigo-50'
                        : 'border-gray-200 bg-white hover:border-indigo-200 hover:bg-gray-50'}`}
                  >
                    <div className={`flex h-9 w-9 items-center justify-center rounded-lg transition-colors
                      ${active ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-500'}`}>
                      {icon}
                    </div>
                    <div>
                      <p className={`text-sm font-semibold ${active ? 'text-indigo-700' : 'text-gray-900'}`}>{label}</p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{desc}</p>
                    </div>
                  </button>
                );
              })}
            </div>

            {/* Couleur */}
            <p className="text-sm font-medium text-gray-700 mb-3">Couleur du bouton</p>
            <div className="flex items-center gap-2 flex-wrap">
              {PRESET_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setBtnColor(color)}
                  title={color}
                  className={`h-8 w-8 rounded-full border-2 transition-transform
                    ${btnColor === color ? 'border-gray-900 scale-110 shadow-md' : 'border-transparent hover:scale-105'}`}
                  style={{ backgroundColor: color }}
                />
              ))}
              {/* Couleur personnalisée */}
              <label
                title="Couleur personnalisée"
                className={`relative flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-2 border-dashed transition-all
                  ${!PRESET_COLORS.includes(btnColor) ? 'border-gray-900 scale-110 shadow-md' : 'border-gray-300 hover:border-gray-500'}`}
                style={!PRESET_COLORS.includes(btnColor) ? { backgroundColor: btnColor } : {}}
              >
                {PRESET_COLORS.includes(btnColor) && (
                  <span className="text-sm font-semibold text-gray-400 select-none">+</span>
                )}
                <input
                  type="color"
                  value={btnColor}
                  onChange={(e) => setBtnColor(e.target.value)}
                  className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                />
              </label>
              <span className="ml-1 font-mono text-xs text-gray-400">{btnColor}</span>
            </div>

            {/* Texte du bouton */}
            <p className="text-sm font-medium text-gray-700 mb-3 mt-6">Texte du bouton</p>
            <input
              type="text"
              value={btnLabel}
              onChange={(e) => setBtnLabel(e.target.value)}
              maxLength={40}
              placeholder="Prendre RDV"
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <p className="mt-1.5 text-xs text-gray-400">{btnLabel.length}/40 caractères</p>
          </div>

          {/* ── Onglets d'installation ──────────────────────────────────── */}
          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">

            {/* Barre d'onglets */}
            <div className="flex border-b border-gray-100 overflow-x-auto">
              {[
                {
                  id: 'web',
                  label: 'Site web',
                  icon: <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" /></svg>,
                },
                {
                  id: 'google',
                  label: 'Google Business',
                  icon: <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor"><path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" fill="#4285F4"/></svg>,
                },
                {
                  id: 'linkedin',
                  label: 'LinkedIn',
                  icon: <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
                },
                {
                  id: 'email',
                  label: 'Signature email',
                  icon: <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth="1.8" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" /></svg>,
                },
                {
                  id: 'whatsapp',
                  label: 'WhatsApp',
                  icon: <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51a12.8 12.8 0 00-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
                },
              ].map(({ id, label, icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setInstallTab(id)}
                  className={`flex items-center gap-2 px-5 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors
                    ${installTab === id
                      ? 'border-indigo-600 text-indigo-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                >
                  {icon}{label}
                </button>
              ))}
            </div>

            {/* Contenu des onglets */}
            <div className="p-6 space-y-5">

              {/* ── Site web ── */}
              {installTab === 'web' && (
                <>
                  <ol className="space-y-4">
                    {[
                      { n: 1, title: "Copiez le snippet ci-dessous", desc: "Un seul bout de code suffit. Il chargera automatiquement le bouton de prise de rendez-vous." },
                      { n: 2, title: <span>Collez-le avant la fermeture <code className="rounded bg-gray-100 px-1 text-xs">&lt;/body&gt;</code> de votre page</span>, desc: "Fonctionne sur tous les CMS : WordPress, Wix, Squarespace, site HTML personnalisé…" },
                      { n: 3, title: "Publiez votre page — c'est tout !", desc: "Le bouton apparaît automatiquement. Couleur et type se modifient depuis ce tableau de bord, sans retoucher le code." },
                    ].map(({ n, title, desc }) => (
                      <li key={n} className="flex gap-4">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-indigo-50 text-xs font-bold text-indigo-700">{n}</div>
                        <div><p className="text-sm font-medium text-gray-900">{title}</p><p className="mt-0.5 text-xs text-gray-500">{desc}</p></div>
                      </li>
                    ))}
                  </ol>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">Votre code d'intégration</p>
                      <button onClick={handleCopy} className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 transition-colors">
                        {copied
                          ? <><svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>Copié !</>
                          : <><svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 17.25v3.375c0 .621-.504 1.125-1.125 1.125h-9.75a1.125 1.125 0 01-1.125-1.125V7.875c0-.621.504-1.125 1.125-1.125H6.75a9.06 9.06 0 011.5.124m7.5 10.376h3.375c.621 0 1.125-.504 1.125-1.125V11.25c0-4.46-3.243-8.161-7.5-8.876a9.06 9.06 0 00-1.5-.124H9.375c-.621 0-1.125.504-1.125 1.125v3.5m7.5 10.375H9.375a1.125 1.125 0 01-1.125-1.125v-9.25m12 6.625v-1.875a3.375 3.375 0 00-3.375-3.375h-1.5a1.125 1.125 0 01-1.125-1.125v-1.5a3.375 3.375 0 00-3.375-3.375H9.75" /></svg>Copier</>
                        }
                      </button>
                    </div>
                    <pre className="rounded-xl bg-gray-950 p-4 text-sm text-green-400 overflow-x-auto leading-relaxed"><code>{snippet}</code></pre>
                  </div>

                  <div className="border-t border-gray-100 pt-5">
                    <p className="text-sm font-medium text-gray-900 mb-1">Envoyer à votre webmaster</p>
                    <p className="text-xs text-gray-500 mb-3">Votre développeur recevra le snippet et les étapes d'installation par email.</p>
                    <form onSubmit={handleSendEmail} className="flex gap-3 flex-wrap">
                      <input type="email" required value={emailContact} onChange={(e) => setEmailContact(e.target.value)} placeholder="email@votrecontact.fr"
                        className="flex-1 min-w-[200px] rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" />
                      <button type="submit" disabled={sendingEmail}
                        className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-600 px-4 py-2 text-sm font-medium text-indigo-600 hover:bg-indigo-50 transition-colors disabled:opacity-50">
                        {sendingEmail ? "Envoi…" : "Envoyer"}
                      </button>
                    </form>
                    {emailSent  && <p className="mt-2 text-xs text-green-600">Email envoyé avec succès !</p>}
                    {emailError && <p className="mt-2 text-xs text-red-600">{emailError}</p>}
                  </div>
                </>
              )}

              {/* ── LinkedIn ── */}
              {installTab === 'linkedin' && (
                <>
                  <p className="text-sm text-gray-600">
                    LinkedIn ne permet pas d'intégrer du code, mais vous pouvez ajouter un lien de prise de RDV directement sur votre profil.
                  </p>

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Votre lien de prise de rendez-vous</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 rounded-xl bg-gray-950 px-4 py-3 text-sm text-green-400 overflow-x-auto whitespace-nowrap">{bookingUrl}</code>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(bookingUrl).then(() => { setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2500); }); }}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2.5 text-xs font-medium text-white hover:bg-indigo-700 transition-colors"
                      >
                        {copiedUrl ? '✓ Copié !' : 'Copier'}
                      </button>
                    </div>
                  </div>

                  <ol className="space-y-4">
                    {[
                      { n: 1, title: "Copiez le lien ci-dessus" },
                      { n: 2, title: "Sur LinkedIn, allez sur votre profil → cliquez sur le crayon « Modifier le profil »" },
                      { n: 3, title: <span>Cliquez sur <strong>« Informations de contact »</strong> → <strong>« Modifier »</strong> → <strong>« Ajouter un site web »</strong></span> },
                      { n: 4, title: <span>Type : <strong>Autre</strong> · Intitulé : <strong>Prendre rendez-vous</strong> · URL : collez le lien</span> },
                      { n: 5, title: "Enregistrez — le lien cliquable apparaît sur votre profil pour tous vos visiteurs." },
                    ].map(({ n, title }) => (
                      <li key={n} className="flex gap-4">
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700">{n}</div>
                        <p className="text-sm text-gray-700 mt-0.5">{title}</p>
                      </li>
                    ))}
                  </ol>

                  <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-xs text-blue-700">
                    Ce lien fonctionne aussi dans vos messages LinkedIn, vos posts, et votre bannière de profil.
                  </div>
                </>
              )}

              {/* ── Signature email ── */}
              {installTab === 'email' && (
                <>
                  <p className="text-sm text-gray-600">
                    Ajoutez un bouton de prise de RDV dans votre signature Gmail ou Outlook. Le code ci-dessous crée un bouton compatible avec tous les clients email.
                  </p>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">Code du bouton (HTML email)</p>
                      <button
                        type="button"
                        onClick={() => {
                          const html = `<table cellpadding="0" cellspacing="0" border="0"><tr><td style="background-color:${btnColor};border-radius:8px;padding:0;"><a href="${bookingUrl}" style="display:inline-block;padding:10px 20px;font-family:Arial,sans-serif;font-size:14px;font-weight:600;color:#ffffff;text-decoration:none;white-space:nowrap;">📅 Prendre rendez-vous</a></td></tr></table>`;
                          navigator.clipboard.writeText(html).then(() => { setCopiedHtml(true); setTimeout(() => setCopiedHtml(false), 2500); });
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 transition-colors"
                      >
                        {copiedHtml ? '✓ Copié !' : 'Copier le code'}
                      </button>
                    </div>
                    <pre className="rounded-xl bg-gray-950 p-4 text-xs text-green-400 overflow-x-auto leading-relaxed whitespace-pre-wrap break-all">
{`<table cellpadding="0" cellspacing="0" border="0">
  <tr>
    <td style="background-color:${btnColor};border-radius:8px;padding:0;">
      <a href="${bookingUrl}"
         style="display:inline-block;padding:10px 20px;font-family:Arial,sans-serif;
                font-size:14px;font-weight:600;color:#ffffff;text-decoration:none;">
        📅 Prendre rendez-vous
      </a>
    </td>
  </tr>
</table>`}
                    </pre>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div className="rounded-xl border border-gray-200 p-4">
                      <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <svg className="h-4 w-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor"><path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z"/><path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z"/></svg>
                        Gmail
                      </p>
                      <ol className="space-y-1.5">
                        {["Paramètres (⚙) → Voir tous les paramètres", "Section « Signature » → créer ou modifier votre signature", "Cliquez sur l'icône « Insérer un lien » et collez l'URL, ou cliquez sur « <> » pour coller le code HTML"].map((s, i) => (
                          <li key={i} className="flex gap-2 text-xs text-gray-600"><span className="font-bold text-gray-400">{i+1}.</span>{s}</li>
                        ))}
                      </ol>
                    </div>
                    <div className="rounded-xl border border-gray-200 p-4">
                      <p className="text-sm font-semibold text-gray-800 mb-2 flex items-center gap-2">
                        <svg className="h-4 w-4 text-blue-700" viewBox="0 0 24 24" fill="currentColor"><path d="M1.5 8.67v8.58a3 3 0 003 3h15a3 3 0 003-3V8.67l-8.928 5.493a3 3 0 01-3.144 0L1.5 8.67z"/><path d="M22.5 6.908V6.75a3 3 0 00-3-3h-15a3 3 0 00-3 3v.158l9.714 5.978a1.5 1.5 0 001.572 0L22.5 6.908z"/></svg>
                        Outlook
                      </p>
                      <ol className="space-y-1.5">
                        {["Fichier → Options → Courrier → Signatures → Nouveau", "Dans l'éditeur de signature, cliquez sur l'icône « Insérer un lien hypertexte »", "Ou collez le code HTML via Insertion → Objet → HTML"].map((s, i) => (
                          <li key={i} className="flex gap-2 text-xs text-gray-600"><span className="font-bold text-gray-400">{i+1}.</span>{s}</li>
                        ))}
                      </ol>
                    </div>
                  </div>
                </>
              )}

              {/* ── Google Business ── */}
              {installTab === 'google' && (
                <>
                  <p className="text-sm text-gray-600">
                    Ajoutez votre lien de prise de RDV sur votre fiche Google Business pour que vos clients puissent réserver directement depuis la recherche Google ou Google Maps.
                  </p>

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Votre lien de prise de rendez-vous</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 rounded-xl bg-gray-950 px-4 py-3 text-sm text-green-400 overflow-x-auto whitespace-nowrap">{bookingUrl}</code>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(bookingUrl).then(() => { setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2500); }); }}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2.5 text-xs font-medium text-white hover:bg-indigo-700 transition-colors"
                      >
                        {copiedUrl ? '✓ Copié !' : 'Copier'}
                      </button>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-200 p-5 space-y-4">
                    <p className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                      <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor"><path d="M12.545 10.239v3.821h5.445c-.712 2.315-2.647 3.972-5.445 3.972a6.033 6.033 0 110-12.064c1.498 0 2.866.549 3.921 1.453l2.814-2.814A9.969 9.969 0 0012.545 2C7.021 2 2.543 6.477 2.543 12s4.478 10 10.002 10c8.396 0 10.249-7.85 9.426-11.748l-9.426-.013z" fill="#4285F4"/></svg>
                      Ajouter le lien sur votre fiche Google Business
                    </p>
                    <ol className="space-y-4">
                      {[
                        {
                          n: 1,
                          title: "Connectez-vous à votre fiche",
                          desc: <span>Rendez-vous sur <a href="https://business.google.com" target="_blank" rel="noreferrer" className="text-indigo-600 underline">business.google.com</a> et sélectionnez votre établissement.</span>,
                        },
                        {
                          n: 2,
                          title: "Accédez à la section « Informations »",
                          desc: "Dans le menu de gauche, cliquez sur « Modifier le profil » ou « Informations ».",
                        },
                        {
                          n: 3,
                          title: "Ajoutez votre site web",
                          desc: "Dans le champ « Site web », collez votre lien de prise de rendez-vous. Il apparaîtra en tant que bouton cliquable sur votre fiche.",
                        },
                        {
                          n: 4,
                          title: "Ajoutez un bouton de réservation (optionnel)",
                          desc: <span>Dans <strong>« Réservations »</strong> → <strong>« Ajouter un lien »</strong>, collez votre URL. Google affichera un bouton <em>« Réserver »</em> directement sur votre fiche dans les résultats de recherche.</span>,
                        },
                        {
                          n: 5,
                          title: "Enregistrez et vérifiez",
                          desc: "Les modifications peuvent prendre quelques heures avant d'apparaître sur Google.",
                        },
                      ].map(({ n, title, desc }) => (
                        <li key={n} className="flex gap-4">
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-50 text-xs font-bold text-blue-700">{n}</div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{title}</p>
                            <p className="mt-0.5 text-xs text-gray-500">{desc}</p>
                          </div>
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="rounded-xl bg-blue-50 border border-blue-100 px-4 py-3 text-xs text-blue-700">
                    Votre lien apparaît aussi dans Google Maps lorsqu'un client recherche votre cabinet. C'est une des meilleures sources de prise de RDV en ligne.
                  </div>
                </>
              )}

              {/* ── WhatsApp ── */}
              {installTab === 'whatsapp' && (
                <>
                  <p className="text-sm text-gray-600">
                    Partagez votre lien de prise de RDV dans vos messages WhatsApp ou ajoutez-le à votre profil WhatsApp Business.
                  </p>

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Votre lien de prise de rendez-vous</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 rounded-xl bg-gray-950 px-4 py-3 text-sm text-green-400 overflow-x-auto whitespace-nowrap">{bookingUrl}</code>
                      <button
                        type="button"
                        onClick={() => { navigator.clipboard.writeText(bookingUrl).then(() => { setCopiedUrl(true); setTimeout(() => setCopiedUrl(false), 2500); }); }}
                        className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-2.5 text-xs font-medium text-white hover:bg-indigo-700 transition-colors"
                      >
                        {copiedUrl ? '✓ Copié !' : 'Copier'}
                      </button>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">Modèle de message à copier</p>
                      <button
                        type="button"
                        onClick={() => {
                          const msg = `Bonjour,\n\nVous pouvez prendre rendez-vous avec moi directement en ligne 👇\n${bookingUrl}\n\nBonne journée`;
                          navigator.clipboard.writeText(msg).then(() => { setCopiedMsg(true); setTimeout(() => setCopiedMsg(false), 2500); });
                        }}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-indigo-700 transition-colors"
                      >
                        {copiedMsg ? '✓ Copié !' : 'Copier le message'}
                      </button>
                    </div>
                    <pre className="rounded-xl bg-gray-50 border border-gray-200 p-4 text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
{`Bonjour,

Vous pouvez prendre rendez-vous avec moi directement en ligne 👇
${bookingUrl}

Bonne journée`}
                    </pre>
                  </div>

                  <div className="rounded-xl bg-green-50 border border-green-100 px-4 py-4 space-y-3">
                    <p className="text-sm font-semibold text-green-800">WhatsApp Business</p>
                    <ol className="space-y-2">
                      {[
                        "Ouvrez WhatsApp Business → Paramètres → Outils commerciaux → Profil",
                        "Champ « Site web » → collez votre lien de prise de RDV",
                        "Vos contacts voient directement le bouton sur votre profil.",
                      ].map((s, i) => (
                        <li key={i} className="flex gap-2 text-xs text-green-700"><span className="font-bold">{i+1}.</span>{s}</li>
                      ))}
                    </ol>
                  </div>
                </>
              )}

            </div>
          </div>
        </div>

        {/* Colonne latérale */}
        <div className="space-y-4">

          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Statut de l'API</h3>
            <StatusBadge ok={apiOk} loading={apiLoading} />
          </div>


          {/* Aperçu cliquable */}
          <button
            onClick={() => setShowPreview(true)}
            className="w-full rounded-2xl border border-gray-200 bg-white p-5 shadow-sm text-left group hover:border-indigo-300 hover:shadow-md transition-all"
          >
            <h3 className="text-sm font-semibold text-gray-700 mb-3 group-hover:text-indigo-700 transition-colors">
              Ce que voit votre visiteur
            </h3>
            <div className="relative h-28 rounded-xl bg-gray-50 border border-dashed border-gray-200 overflow-hidden group-hover:border-indigo-300 transition-colors">
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="flex flex-col items-center gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
                  <svg className="h-6 w-6 text-indigo-500" fill="none" viewBox="0 0 24 24" strokeWidth="1.5" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="text-xs font-medium text-indigo-600">Voir l'aperçu</span>
                </div>
                <p className="text-xs text-gray-300 italic group-hover:opacity-0 transition-opacity absolute">Aperçu du site</p>
              </div>
              <div className={`absolute ${btnMode === 'inline' ? 'inset-x-3 bottom-3' : 'bottom-3 right-3'}`}>
                <div className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-semibold text-white shadow-lg pointer-events-none ${btnMode === 'inline' ? 'w-full justify-center' : ''}`}
                  style={{ backgroundColor: btnColor }}>
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
                  </svg>
                  {btnLabel || 'Prendre RDV'}
                </div>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-400 group-hover:text-indigo-500 transition-colors">
              Cliquez pour prévisualiser l'expérience complète →
            </p>
          </button>

          <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Besoin d'aide ?</h3>
            <p className="text-xs text-gray-500 mb-3">Notre équipe peut vous aider à installer le widget sur votre site.</p>
            <a href="https://wa.me/0757831364" target="_blank" rel="noreferrer" className="text-xs font-medium text-indigo-600 hover:text-indigo-800">
              Contacter le support →
            </a>
          </div>
        </div>
      </div>
    </Container>
  );
}

export default WidgetRdv;
